import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Eye, EyeOff, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", license_key: "", valid_until: "", notes: "" };

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const m = /^\d{4}-\d{2}-\d{2}$/.exec(dateStr.trim());
  if (!m) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + "T00:00:00");
  if (isNaN(target.getTime())) return null;
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

export default function Software() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=create, {id,...}=edit
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [revealed, setRevealed] = useState(() => new Set());
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/software");
      setItems(data);
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load software");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (row) => {
    setForm({
      name: row.name || "",
      license_key: row.license_key || "",
      valid_until: row.valid_until || "",
      notes: row.notes || "",
    });
    setEditing(row);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const onSave = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Software name is required"); return; }
    setBusy(true);
    try {
      if (editing && editing.id) {
        await api.put(`/software/${editing.id}`, form);
        toast.success("Software updated");
      } else {
        await api.post("/software", form);
        toast.success("Software added");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!deleteRow) return;
    try {
      await api.delete(`/software/${deleteRow.id}`);
      toast.success("Software deleted");
      setDeleteRow(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleReveal = (id) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copyKey = async (id, key) => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="software-page">
      <PageHeader
        eyebrow="[09] Software"
        title="Software Licenses"
        subtitle="A manual register of software licenses, keys, expiry dates, and notes."
        actions={
          <Button
            data-testid="add-software-btn"
            onClick={openCreate}
            className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
          >
            <Plus className="w-3.5 h-3.5 mr-2" /> Add Software
          </Button>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="software-table">
            <thead>
              <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                <Th>Software Name</Th>
                <Th>License Key</Th>
                <Th>Valid Until</Th>
                <Th>Notes</Th>
                <Th align="right"></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center">
                  <div className="font-mono text-xs text-[#7A7A7A] mb-3">No software registered yet.</div>
                  <button onClick={openCreate} data-testid="empty-add-btn" className="font-mono text-xs uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F]">+ Add your first entry</button>
                </td></tr>
              )}
              {items.map((row) => {
                const d = daysUntil(row.valid_until);
                let expiryTone = "text-[#3A3A3A]";
                if (d !== null) {
                  if (d < 0) expiryTone = "text-[#B91C1C]";
                  else if (d <= 30) expiryTone = "text-[#B45309]";
                  else expiryTone = "text-[#2D7A4F]";
                }
                const shown = revealed.has(row.id);
                return (
                  <tr key={row.id} data-testid={`software-row-${row.id}`} className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3] transition-colors">
                    <Td><span className="text-[#0F0F0F]">{row.name}</span></Td>
                    <Td>
                      {row.license_key ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-[#3A3A3A] select-all" data-testid={`license-key-${row.id}`}>
                            {shown ? row.license_key : "•".repeat(Math.min(Math.max(row.license_key.length, 6), 16))}
                          </span>
                          <button
                            onClick={() => toggleReveal(row.id)}
                            data-testid={`reveal-key-${row.id}`}
                            title={shown ? "Hide" : "Show"}
                            className="p-1 text-[#7A7A7A] hover:text-[#0F0F0F]"
                          >
                            {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => copyKey(row.id, row.license_key)}
                            data-testid={`copy-key-${row.id}`}
                            title="Copy key"
                            className="p-1 text-[#7A7A7A] hover:text-[#0F0F0F]"
                          >
                            {copiedId === row.id ? <Check className="w-3.5 h-3.5 text-[#2D7A4F]" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      ) : (
                        <span className="font-mono text-xs text-[#9A9A9A]">—</span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className={`font-mono text-xs ${expiryTone}`}>{row.valid_until || "—"}</span>
                        {d !== null && d < 0 && <span className="font-mono text-[10px] uppercase text-[#B91C1C]">Expired</span>}
                        {d !== null && d >= 0 && d <= 30 && <span className="font-mono text-[10px] uppercase text-[#B45309]">{d}d left</span>}
                      </div>
                    </Td>
                    <Td>
                      <span className="text-[#3A3A3A] block max-w-[320px] truncate" title={row.notes || ""}>
                        {row.notes || <span className="font-mono text-xs text-[#9A9A9A]">—</span>}
                      </span>
                    </Td>
                    <Td align="right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(row)}
                          data-testid={`edit-software-${row.id}`}
                          className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F]"
                          title="Edit"
                        ><Pencil className="w-3.5 h-3.5" /></button>
                        <button
                          onClick={() => setDeleteRow(row)}
                          data-testid={`delete-software-${row.id}`}
                          className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]"
                          title="Delete"
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="font-mono text-xs text-[#9A9A9A]">Total: {items.length} {items.length === 1 ? "entry" : "entries"}</div>
      </div>

      {/* Create/edit modal */}
      <AlertDialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">
              {editing && editing.id ? "Edit software" : "Add software"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              Capture a software product, its license key, expiry, and any notes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="uppercase-label">Software Name *</Label>
              <Input
                data-testid="form-software-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Adobe Acrobat Pro"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                autoFocus
              />
            </div>
            <div>
              <Label className="uppercase-label">License Key</Label>
              <Input
                data-testid="form-software-key"
                value={form.license_key}
                onChange={(e) => setForm({ ...form, license_key: e.target.value })}
                placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Valid Until</Label>
              <Input
                data-testid="form-software-expiry"
                type="date"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
            <div>
              <Label className="uppercase-label">Notes</Label>
              <Textarea
                data-testid="form-software-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Seat count, assigned user, renewal contact, anything useful…"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="form-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="form-save-btn"
              disabled={busy || !form.name.trim()}
              onClick={(e) => { e.preventDefault(); onSave(); }}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-50"
            >{busy ? "Saving…" : (editing && editing.id ? "Save changes" : "Add")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Delete this software entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">
              &quot;{deleteRow?.name}&quot; will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-software-btn" onClick={onDelete} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-${align} font-medium`}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td className={`px-4 py-3 text-${align}`}>{children}</td>;
}
